import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import type { EmailOtpType } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/auth';

/**
 * Landing page for any Supabase auth redirect.
 *
 * Three flows arrive here (bootstrap.ts forwards them all to /auth/callback):
 *   1. ?token_hash=X&type=invite|recovery|... — Dashboard invites and password
 *      recovery with the modern email template. detectSessionInUrl can't
 *      handle these; we call verifyOtp explicitly.
 *   2. ?code=X — PKCE code from signInWithOtp magic link. Auto-exchanged by
 *      supabase-js because detectSessionInUrl + flowType=pkce is on.
 *   3. #access_token=X&refresh_token=Y — implicit/hash flow. Also auto-handled.
 *
 * On success, the auth store's onAuthStateChange listener fires and we
 * navigate to /business. On failure (expired token, missing material, etc.)
 * we render a friendly error with a link back to /login.
 */

const VALID_OTP_TYPES: readonly EmailOtpType[] = [
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
];

function parseOtpType(raw: string | null): EmailOtpType | null {
  if (!raw) return null;
  return (VALID_OTP_TYPES as readonly string[]).includes(raw)
    ? (raw as EmailOtpType)
    : null;
}

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const errParam =
      search.get('error_description') ||
      search.get('error') ||
      hash.get('error_description') ||
      hash.get('error');

    if (errParam) {
      setErrorMsg(decodeURIComponent(errParam.replace(/\+/g, ' ')));
      return;
    }

    const tokenHash = search.get('token_hash');
    const otpType = parseOtpType(search.get('type'));
    if (tokenHash && otpType) {
      // Server-initiated flow (Dashboard invite, recovery, signup confirm)
      // when the email template was customized to use {{ .TokenHash }}.
      // verifyOtp consumes the token_hash and creates a session; we navigate
      // explicitly on success rather than waiting for onAuthStateChange to
      // race with the second effect.
      supabase.auth
        .verifyOtp({ token_hash: tokenHash, type: otpType })
        .then(({ data, error }) => {
          if (error) {
            // eslint-disable-next-line no-console
            console.error('[auth] verifyOtp failed', { type: otpType, error });
            setErrorMsg(error.message);
            return;
          }
          if (data.session) {
            navigate('/business', { replace: true });
          }
        });
      return;
    }

    // PKCE ?code= or implicit #access_token= — both auto-handled by
    // detectSessionInUrl when the client initialized. Just wait.
    const hasExchangeMaterial =
      search.has('code') || hash.has('access_token') || hash.has('refresh_token');
    if (!hasExchangeMaterial) {
      setErrorMsg('קישור הכניסה חסר. חזור למסך הכניסה ונסה שוב.');
      return;
    }

    const timer = setTimeout(() => {
      if (!useAuthStore.getState().session) {
        setErrorMsg('הקישור אינו תקף או שפג תוקפו. נסה להיכנס שוב.');
      }
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (session) {
      navigate('/business', { replace: true });
    }
  }, [session, navigate]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-navy-dark via-navy to-navy-light flex items-center justify-center px-4"
      dir="rtl"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
        {errorMsg ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-7 h-7 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-navy mb-2">הכניסה נכשלה</h1>
            <p className="text-sm text-slate-600 mb-6">{errorMsg}</p>
            <Link
              to="/login"
              className="inline-block bg-business-primary hover:bg-business-primary/90 text-white font-medium px-5 py-2.5 rounded-lg transition"
            >
              חזרה למסך כניסה
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-business-primary animate-spin mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-navy mb-1">מאמת כניסה…</h1>
            <p className="text-sm text-slate-600">עוד רגע נעביר אותך פנימה.</p>
          </>
        )}
      </div>
    </div>
  );
}
